import { useState, type FormEvent } from "react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah8utjp14a1h3mr6fl0";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  useSeo({
    title: "联系我们 | 爆瓜爆料社",
    description:
      "联系爆瓜爆料社：匿名投稿爆料线索、反馈内容错误或提出合作咨询。附投稿表单与联系邮箱，编辑会在 24 小时内响应。",
    keywords: "爆瓜爆料,联系我们,投稿爆料,线索提交,内容纠错",
    canonical: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们",
      url: "https://app-test.cn/contact",
      description: "向爆瓜爆料社投稿爆料线索或反馈内容问题。",
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const name = String(payload.get("name") ?? "").trim();
    const email = String(payload.get("email") ?? "").trim();
    const content = String(payload.get("content") ?? "").trim();

    if (!name) {
      setStatus("error");
      setFormError("请填写你的称呼，方便我们知道该怎么称呼你。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式好像不太对，请检查后再提交。");
      return;
    }
    if (!content) {
      setStatus("error");
      setFormError("爆料内容不能为空，简单描述几句也可以。");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setFormError("爆料内容请控制在 500 字以内，重要线索可以先给个大概。");
      return;
    }

    const body = new URLSearchParams();
    payload.delete("website_alt");
    payload.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        if (isSpam) {
          setFormError("提交内容被识别为异常信息，请检查后重新提交。");
        } else {
          setFormError(serverMsg || "提交失败，请稍后再试。");
        }
      }
    } catch {
      setStatus("error");
      setFormError("网络出现异常，提交没有成功，请稍后重试。");
    }
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "联系我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            有瓜要爆？这里就是入口
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            支持匿名投稿。留下大致的时间、地点和经过即可，我们会先做一轮基础核实，
            确认有必要跟进时会通过你留下的邮箱联系。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8">
              {status === "success" ? (
                <div className="py-10 text-center">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-checkbox-circle-fill text-4xl" aria-hidden="true" />
                  </span>
                  <h2 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
                    收到啦，谢谢你的瓜
                  </h2>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-foreground-600">
                    编辑会在 24 小时内查看这条线索。如果信息足够具体，我们会尽快安排核实并整理成内容。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300/80 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-add-line" aria-hidden="true" />
                    再投一条
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-bold text-foreground-950">投稿 / 反馈表</h2>
                  <p className="mt-2 text-sm text-foreground-500">带 * 的为必填项。</p>

                  <form
                    data-readdy-form
                    id="baogua-contact-form"
                    onSubmit={handleSubmit}
                    className="mt-6 flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div className="flex flex-col gap-2">
                        <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                          你的称呼 <span className="text-primary-600">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          autoComplete="off"
                          placeholder="可以是昵称或化名"
                          className="rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                          联系邮箱 <span className="text-primary-600">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="off"
                          placeholder="用于编辑跟你确认线索"
                          className="rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="type" className="text-sm font-medium text-foreground-800">
                        线索类型
                      </label>
                      <select
                        id="type"
                        name="type"
                        defaultValue="star"
                        className="cursor-pointer rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      >
                        {categories.map((c) => (
                          <option key={c.slug} value={c.slug}>
                            {c.name}
                          </option>
                        ))}
                        <option value="other">其他 / 内容纠错</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="content" className="text-sm font-medium text-foreground-800">
                        爆料内容 <span className="text-primary-600">*</span>
                      </label>
                      <textarea
                        id="content"
                        name="content"
                        rows={6}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="时间、地点、大致经过，越具体越容易被采用（最多 500 字）"
                        className="resize-none rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                      <span className="self-end text-xs text-foreground-500">{charCount}/500</span>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="source" className="text-sm font-medium text-foreground-800">
                        资料来源（选填）
                      </label>
                      <input
                        id="source"
                        name="source"
                        type="text"
                        autoComplete="off"
                        placeholder="如：现场目击 / 公开动态 / 媒体报道链接"
                        className="rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                    </div>

                    <div className="form-extra-note" aria-hidden="true">
                      <label htmlFor="website_alt">请勿填写此栏</label>
                      <input
                        id="website_alt"
                        name="website_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {status === "error" && formError && (
                      <p className="flex items-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                        <i className="ri-error-warning-line" aria-hidden="true" />
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i
                        className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}
                        aria-hidden="true"
                      />
                      {status === "submitting" ? "正在提交…" : "提交爆料"}
                    </button>
                    <p className="text-xs leading-5 text-foreground-500">
                      提交即表示你确认所提供信息为本人所知，并同意编辑在核实后整理发布。
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系方式</h2>
              <ul className="mt-4 flex flex-col gap-4 text-sm text-foreground-600">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-800">投稿邮箱</p>
                    <p className="mt-0.5 text-xs">tip@baogua-portal.example.com</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-time-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-800">响应时间</p>
                    <p className="mt-0.5 text-xs">工作日 24 小时内查看线索</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-shield-user-line" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-800">匿名保护</p>
                    <p className="mt-0.5 text-xs">不公开来源信息，仅用于核实</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-100/70 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">什么样的线索更容易被采用</h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm leading-6 text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true" />
                  有明确时间、地点或可查证的公开信息
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true" />
                  能提供截图、链接等辅助材料
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true" />
                  与已发生事件相关，而非纯粹的猜测
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}